import { Layout } from "@/components/layout";
import { SEOHead } from "@/components/seo-head";
import { CAREERS, BRAND } from "@/lib/content";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Mail } from "lucide-react";
import { submitForm, SubmitFormError } from "@/lib/submit-form";
import { track } from "@/lib/analytics";
import { motion } from "framer-motion";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";

const applySchema = z.object({
  name: z.string().min(2, "Name is required"),
  email: z.string().email("Valid email is required"),
  role: z.string().min(1, "Role is required"),
  links: z.string().optional(),
  notes: z.string().optional(),
});

export default function CareersPage() {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const form = useForm<z.infer<typeof applySchema>>({
    resolver: zodResolver(applySchema),
    defaultValues: {
      name: "",
      email: "",
      role: "General application",
      links: "",
      notes: "",
    },
  });

  const openApplyForm = () => {
    form.reset({
      name: "",
      email: "",
      role: "General application",
      links: "",
      notes: "",
    });
    setDialogOpen(true);
  };

  async function onSubmit(values: z.infer<typeof applySchema>) {
    setIsSubmitting(true);
    try {
      await submitForm("careers", values);
      track("marketing_form_submitted", {
        source: "careers",
        role: values.role,
      });
      toast({
        title: "Thanks for reaching out",
        description: "We'll review your message and get back to you soon.",
      });
      setDialogOpen(false);
    } catch (err) {
      if (err instanceof SubmitFormError && err.fieldErrors) {
        for (const [name, message] of Object.entries(err.fieldErrors)) {
          form.setError(name as keyof z.infer<typeof applySchema>, {
            type: "server",
            message,
          });
        }
      }
      toast({
        title: "Submission failed",
        description:
          err instanceof Error
            ? err.message
            : "Please review the form and try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Layout>
      <SEOHead title={CAREERS.seo.title} description={CAREERS.seo.description} />

      <section className="pt-24 pb-20 md:pt-32 md:pb-24 bg-slate-50 dark:bg-navy/30 border-b border-border/50">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl"
          >
            <div className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 mb-4 tracking-wider uppercase">
              {CAREERS.eyebrow}
            </div>
            <h1 className="text-display mb-6 text-navy dark:text-white">
              {CAREERS.title}
            </h1>
            <p className="text-body-lg text-slate-600 dark:text-slate-300">
              {CAREERS.subtitle}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 md:py-24">
        <div className="container mx-auto px-4 md:px-6">
          <h2 className="text-h2 mb-12 text-navy dark:text-white text-center">Benefits & perks</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {CAREERS.benefits.map((benefit, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-card rounded-2xl p-6 border shadow-sm"
              >
                <div className="flex items-center gap-3 mb-4">
                  <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                  <h3 className="text-lg font-bold">{benefit.title}</h3>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {benefit.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-32 bg-slate-50 dark:bg-navy/30 border-y border-border/50">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl text-center">
          <h2 className="text-h2 mb-6 text-navy dark:text-white">Open roles</h2>
          <p className="text-body-lg text-slate-600 dark:text-slate-400 mb-8">
            {CAREERS.rolesIntro}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button
              size="lg"
              onClick={openApplyForm}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              {CAREERS.generalApplyCta}
            </Button>
            <a
              href={`mailto:${BRAND.email}?subject=Hello%20from%20a%20prospective%20teammate`}
              className="inline-flex items-center gap-2 text-sm font-semibold text-indigo-600 dark:text-indigo-400 hover:underline"
            >
              <Mail className="h-4 w-4" />
              Or email {BRAND.email}
            </a>
          </div>
        </div>
      </section>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Send us an introduction</DialogTitle>
            <DialogDescription>
              Tell us a little about you and what you'd like to work on. We'll read every message.
            </DialogDescription>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 mt-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Full name</FormLabel>
                    <FormControl>
                      <Input placeholder="Jane Doe" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email address</FormLabel>
                    <FormControl>
                      <Input placeholder="jane@example.com" type="email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="links"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>LinkedIn / GitHub / portfolio</FormLabel>
                    <FormControl>
                      <Input placeholder="https://..." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>A brief note</FormLabel>
                    <FormControl>
                      <Textarea placeholder="What would you like to work on, and why ClauseArk?" rows={4} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="pt-4 flex justify-end">
                <Button type="button" variant="outline" className="mr-2" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Submitting..." : "Send"}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
